import { BaseCampaignItem, BaseCampaignItemProps } from './BaseCampaignItem';
import { FreightCondition } from '@retail/products/types';
import { CampaignPrice } from './CampaignPrice';
import { MemberCampaignItemDTO, MemberCampaignPatchItemDTO } from '../dto';
import { CampaignUser } from './CampaignUser';
import { calculateSalesCoverageFromCostAndSalesPrice } from '@retail/utils';

interface MemberCampaignItemProps extends BaseCampaignItemProps {
  primaryDiscountPercentage?: number;
  memberCampaignSalesUnitPrices?: CampaignPrice[];
  localPurchaseUnitPrices?: CampaignPrice[];
  localFreightCondition?: FreightCondition;
  localCostUnitPrices?: CampaignPrice[];
  localSalesUnitPrices?: CampaignPrice[];
}

export class MemberCampaignItem extends BaseCampaignItem {
  primaryDiscountPercentage?: number;
  memberCampaignSalesUnitPrices?: CampaignPrice[];
  localPurchaseUnitPrices?: CampaignPrice[];
  localFreightCondition?: FreightCondition;
  localCostUnitPrices?: CampaignPrice[];
  localSalesUnitPrices?: CampaignPrice[];

  constructor(props: MemberCampaignItemProps) {
    super(props);
    this.primaryDiscountPercentage = props.primaryDiscountPercentage;
    this.memberCampaignSalesUnitPrices = props.memberCampaignSalesUnitPrices;
    this.localPurchaseUnitPrices = props.localPurchaseUnitPrices;
    this.localFreightCondition = props.localFreightCondition;
    this.localCostUnitPrices = props.localCostUnitPrices;
    this.localSalesUnitPrices = props.localSalesUnitPrices;
  }

  override getSalesPriceForCampaignUnit = () =>
    (this.memberCampaignSalesUnitPrices ?? this.campaignSalesUnitPrices)?.find(
      (price) => price.unit.unit === this.campaignUnit
    );

  getOriginalPriceForCampaignUnit = () =>
    this.campaignSalesUnitPrices?.find((price) => price.unit.unit === this.campaignUnit);

  getLocalPurchasePriceForCampaignUnit = () =>
    this.localPurchaseUnitPrices?.find((price) => price.unit.unit === this.campaignUnit);

  getLocalCostPriceForCampaignUnit = () =>
    this.localCostUnitPrices?.find((price) => price.unit.unit === this.campaignUnit);

  getLocaSalesPriceForCampaignUnit = () =>
    this.localSalesUnitPrices?.find((price) => price.unit.unit === this.campaignUnit);

  getLocalSalesCoverage = (): number | undefined => {
    const localCostPrice = this.getLocalCostPriceForCampaignUnit();
    const localSalesPrice = this.getLocaSalesPriceForCampaignUnit();

    return localCostPrice && localSalesPrice
      ? calculateSalesCoverageFromCostAndSalesPrice(
          localCostPrice.priceWithoutVat,
          localSalesPrice.priceWithoutVat
        )
      : undefined;
  };

  getFormattedLocalSalesCoverage = (): string | undefined => {
    const salesCoverage = this.getLocalSalesCoverage();
    return salesCoverage ? `${Number(salesCoverage.toFixed(2))}%` : undefined;
  };

  getPatchCampaignItem = (): MemberCampaignPatchItemDTO => this.getBasePatchCampaignItem();

  static fromDto(dto: MemberCampaignItemDTO, locale: string): MemberCampaignItem {
    return new MemberCampaignItem({
      id: dto.id,
      mgItemNumber: dto.mgItemNumber,
      mgSupplierItemId: dto.mgSupplierItemId,
      primaryText: dto.primaryText,
      secondaryText: dto.secondaryText,
      mgSupplierId: dto.mgSupplierId,
      mgSupplierName: dto.mgSupplierName,
      finfoNumber: dto.finfoNumber,
      nobbNumber: dto.nobbNumber,
      groupNumber: dto.groupNumber,
      groupDescription: dto.groupDescription,
      discountGroup: dto.discountGroup,
      primaryPackageUnit: dto.primaryPackageUnit,
      campaignUnit: dto.campaignUnit,
      isSalesPriceOverridable: dto.isSalesPriceOverridable,
      primaryDiscountPercentage: dto.primaryDiscountPercentage,
      campaignSalesUnitPrices: CampaignPrice.fromDtoToList(dto.campaignSalesPrice, locale),
      memberCampaignSalesUnitPrices: dto.memberCampaignSalesPrice
        ? CampaignPrice.fromDtoToList(dto.memberCampaignSalesPrice, locale)
        : undefined,
      campaignPurchaseUnitPrices: dto.purchasePrice
        ? CampaignPrice.fromDtoToList(dto.purchasePrice, locale)
        : undefined,
      purchasePeriodFrom: dto.purchasePeriodFrom,
      purchasePeriodTo: dto.purchasePeriodTo,
      createdAt: dto.createdAt,
      createdBy: dto.createdBy ? CampaignUser.fromDto(dto.createdBy) : undefined,
      localPurchaseUnitPrices: dto.localPurchasePrice
        ? CampaignPrice.fromDtoToList(dto.localPurchasePrice, locale)
        : undefined,
      localFreightCondition: dto.localFreightCondition
        ? FreightCondition.fromDto(dto.localFreightCondition)
        : undefined,
      localCostUnitPrices: dto.localCostPrice
        ? CampaignPrice.fromDtoToList(dto.localCostPrice, locale)
        : undefined,
      localSalesUnitPrices: dto.localSalesPrice
        ? CampaignPrice.fromDtoToList(dto.localSalesPrice, locale)
        : undefined,
    });
  }
}
